import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import useMyUser from "~/auth/useMyUser";
import useLanguagePreferenceMutations from "~/hooks/mutations/useLanguagePreferenceMutations";
import useSaveLocalPreference from "~/hooks/useSaveLocalPreference";
import {
	type AppLanguage,
	markLocalLanguageChange,
	normalizeLanguage,
	preferenceFromLanguage,
	writeCachedLanguage,
	writeCachedLanguageChecked
} from "~/i18n/languagePreference";

interface UseLanguagePreferenceReturn {
	hasUserRecord: boolean;
	isSaving: boolean;
	language: AppLanguage;
	setLanguage: (language: AppLanguage) => Promise<boolean>;
}

const useLanguagePreference = (): UseLanguagePreferenceReturn => {
	const saveLocalPreference = useSaveLocalPreference();
	const { i18n } = useTranslation("common");
	const { isSaving, setLanguagePreference } = useLanguagePreferenceMutations();
	const { hasUserRecord } = useMyUser();
	const language = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);

	const setLanguage = useCallback(
		async (next: AppLanguage): Promise<boolean> => {
			const previous = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);
			if (next === previous) return true;

			if (!hasUserRecord && !saveLocalPreference(() => writeCachedLanguageChecked(next))) return false;

			markLocalLanguageChange();
			if (hasUserRecord) writeCachedLanguage(next);
			await i18n.changeLanguage(next);

			// wsapi stores the language on the user record, so without one the choice can only live on this device.
			if (!hasUserRecord) return true;

			const saved = await setLanguagePreference(preferenceFromLanguage(next));
			if (!saved) {
				writeCachedLanguage(previous);
				await i18n.changeLanguage(previous);
			}
			return saved;
		},
		[hasUserRecord, i18n, saveLocalPreference, setLanguagePreference]
	);

	return { hasUserRecord, isSaving, language, setLanguage };
};

export default useLanguagePreference;
